import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

/** White, 8px radius, 18px semibold title, footer buttons on the right. */
export function DialogContent({
  title,
  description,
  header,
  children,
  footer,
  className,
  dismissible = true,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Pills and other context under the title, inside the header. */
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  /** False for a dialog that has to be answered: Escape and a click outside do nothing. */
  dismissible?: boolean;
}) {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay
        className={cn(
          'fixed inset-0 z-50 bg-[color:var(--ts-overlay)]',
          'data-[state=open]:anim-fade-in data-[state=closed]:anim-fade-out',
        )}
      />
      <RadixDialog.Content
        className={cn(
          'fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-[540px] -translate-x-1/2',
          '-translate-y-1/2 flex-col rounded-lg border border-[color:var(--ts-border-soft)] bg-surface',
          'shadow-float outline-none',
          'data-[state=open]:anim-pop-in data-[state=closed]:anim-pop-out',
          className,
        )}
        onEscapeKeyDown={dismissible ? undefined : (event) => event.preventDefault()}
        onInteractOutside={dismissible ? undefined : (event) => event.preventDefault()}
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <header
          className={cn(
            'px-6 pt-6 pb-4',
            header && 'border-b border-[color:var(--ts-border-soft)]',
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-xl font-semibold text-gray-3">{title}</RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-base text-muted-foreground">
                  {description}
                </RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close
              aria-label="Close"
              className={cn(
                'mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-1',
                'transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)] hover:text-primary',
              )}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </RadixDialog.Close>
          </div>
          {header ? <div className="mt-3 flex flex-wrap items-center gap-2">{header}</div> : null}
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-3 border-t border-[color:var(--ts-border-soft)] px-6 py-5">
            {footer}
          </footer>
        ) : null}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
